<!--
/**
 * APCWO customisation for OrangeHRM — Personal Review module.
 *
 * Licensed under the GNU General Public License v2.0 or later, the same terms
 * as OrangeHRM itself. See the LICENSE file at the project root.
 */
-->

<template>
  <div class="orangehrm-background-container apcwo-pr-root">
    <hr-nav active="reviews" :is-hr="isHr" />
    <div v-if="review" class="orangehrm-card-container">
      <div class="apcwo-pr-form__head">
        <div>
          <oxd-text tag="h6" class="orangehrm-main-title">
            {{ review.employee.name }}
          </oxd-text>
          <div class="apcwo-pr-form__sub">
            {{ review.cycle.name }} · {{ review.team || 'No team' }} ·
            {{ review.roleTitle || 'No role' }}
          </div>
        </div>
        <div class="apcwo-pr-form__head-right">
          <status-badge :status="review.status" />
        </div>
      </div>

      <div class="apcwo-pr-detail-actions">
        <download-buttons
          v-if="review.permissions.downloadEmployee"
          :review-id="review.id"
          scope="employee"
        />
        <span
          v-if="review.permissions.downloadEmployee"
          class="apcwo-pr-card__meta"
        >
          employee copy
        </span>
        <download-buttons
          v-if="review.permissions.downloadHr"
          :review-id="review.id"
          scope="hr"
        />
        <span v-if="review.permissions.downloadHr" class="apcwo-pr-card__meta">
          full document
        </span>
      </div>
      <div class="apcwo-pr-detail-actions">
        <oxd-button
          v-if="review.permissions.complete"
          label="Send final review to employee"
          display-type="secondary"
          :disabled="isLoading"
          @click="runAction('complete')"
        />
        <oxd-button
          v-if="review.permissions.reopen"
          label="Reopen evaluation"
          display-type="ghost"
          :disabled="isLoading"
          @click="runAction('reopen')"
        />
        <oxd-button
          v-if="review.permissions.unlock && !unlocking"
          label="Unlock for employee"
          display-type="label-danger"
          :disabled="isLoading"
          @click="unlocking = true"
        />
      </div>
      <div v-if="unlocking" class="apcwo-pr-banner apcwo-pr-banner--warn">
        The employee will be able to edit and re-submit. The review will go back
        to draft and the reason is recorded.
        <oxd-input-field
          v-model="unlockReason"
          type="textarea"
          label="Reason (required)"
          class="apcwo-pr-field"
        />
        <div class="apcwo-pr-confirm">
          <oxd-button
            label="Unlock"
            display-type="label-danger"
            :disabled="isLoading || !unlockReason.trim()"
            @click="runAction('unlock', unlockReason)"
          />
          <oxd-button
            label="Cancel"
            display-type="ghost"
            @click="unlocking = false"
          />
        </div>
      </div>

      <oxd-divider />
      <ol class="apcwo-pr-steps">
        <li v-for="t in tabs" :key="t.key">
          <button
            type="button"
            :class="['apcwo-pr-steps__item', {'is-active': tab === t.key}]"
            @click="tab = t.key"
          >
            {{ t.label }}
          </button>
        </li>
      </ol>

      <!-- Employee submission (read-only) -->
      <section v-if="tab === 'employee'">
        <div v-if="!review.employeeData" class="apcwo-pr-banner">
          The employee has not submitted this review yet, so their answers are
          not visible.
        </div>
        <template v-else>
          <h3 class="apcwo-pr-section">{{ schema.lifeVision.title }}</h3>
          <div class="apcwo-pr-read">
            <strong>MY PERSONAL LIFE VISION</strong>
            <p>{{ review.employeeData.lifeVision || '—' }}</p>
            <strong>{{ schema.apcHelpLabel }}</strong>
            <p>{{ review.employeeData.apcHelp || '—' }}</p>
          </div>
          <h3 class="apcwo-pr-section">{{ schema.spiritual.title }}</h3>
          <div class="apcwo-pr-read">
            <div
              v-for="(g, i) in review.employeeData.spiritualGoals"
              :key="'s' + i"
              class="apcwo-pr-read__pair"
            >
              <p>
                <strong>{{ i + 1 }}.</strong> {{ g.objective || '—' }}
              </p>
              <p>{{ g.action || '—' }}</p>
            </div>
          </div>
          <h3 class="apcwo-pr-section">{{ schema.professional.title }}</h3>
          <div class="apcwo-pr-read">
            <div
              v-for="(g, i) in review.employeeData.professionalGoals"
              :key="'p' + i"
              class="apcwo-pr-read__pair"
            >
              <p>
                <strong>{{ i + 1 }}.</strong> {{ g.objective || '—' }}
              </p>
              <p>{{ g.action || '—' }}</p>
            </div>
          </div>
          <template v-for="(sec, key) in schema.textSections" :key="key">
            <h3 class="apcwo-pr-section">{{ sec.title }}</h3>
            <div class="apcwo-pr-read">
              <p>{{ review.employeeData[key] || '—' }}</p>
            </div>
          </template>
          <h3 class="apcwo-pr-section">EMPLOYEE EVALUATION</h3>
          <div class="apcwo-pr-read">
            <div
              v-for="r in schema.ratings"
              :key="r.key"
              class="apcwo-pr-rate-row"
            >
              <span>{{ r.label }}</span>
              <rating-input
                :model-value="review.employeeData.ratings[r.key]"
                readonly
              />
            </div>
            <template v-for="q in schema.evaluationText" :key="q.key">
              <strong>{{ q.label.toUpperCase() }}</strong>
              <p>{{ review.employeeData[q.key] || '—' }}</p>
            </template>
          </div>
        </template>
      </section>

      <!-- A manager's own evaluation -->
      <section v-if="tab === 'mine'">
        <div v-if="!review.permissions.editManager" class="apcwo-pr-banner">
          This review is no longer open for manager evaluation.
        </div>
        <template v-if="mine">
          <evaluation-form
            :model="mine"
            :schema="schema"
            :editable="review.permissions.editManager"
          />
          <oxd-divider />
          <div class="apcwo-pr-actions">
            <oxd-button
              v-if="review.permissions.editManager"
              label="Save my evaluation"
              display-type="secondary"
              :disabled="isLoading"
              @click="saveSection('manager', mine)"
            />
          </div>
        </template>
      </section>

      <!-- Every manager's evaluation (HR) -->
      <section v-if="tab === 'managers'">
        <div v-if="!review.managerEvaluations.length" class="apcwo-pr-banner">
          This employee has no managers on record.
        </div>
        <div
          v-for="m in review.managerEvaluations"
          :key="m.empNumber"
          class="apcwo-pr-manager"
        >
          <h3 class="apcwo-pr-section">{{ m.name }}</h3>
          <div v-if="!m.data" class="apcwo-pr-banner">
            Has not recorded an evaluation yet.
          </div>
          <template v-else>
            <div class="apcwo-pr-card__meta">Saved {{ m.savedAt }}</div>
            <evaluation-form :model="m.data" :schema="schema" />
          </template>
        </div>
      </section>

      <!-- Final review (HR) -->
      <section v-if="tab === 'hr'">
        <div
          v-if="!hrEditable && review.status !== 'completed'"
          class="apcwo-pr-banner"
        >
          The final review opens once the employee has submitted.
        </div>
        <template v-if="hr">
          <evaluation-form
            :model="hr"
            :schema="schema"
            :editable="hrEditable"
          />
          <oxd-divider />
          <div class="apcwo-pr-actions">
            <oxd-button
              v-if="hrEditable"
              label="Save final review"
              display-type="secondary"
              :disabled="isLoading"
              @click="saveSection('hr', hr)"
            />
            <oxd-button
              v-if="review.permissions.complete"
              label="Save & send to employee"
              display-type="ghost"
              :disabled="isLoading"
              @click="saveAndComplete"
            />
          </div>
        </template>
      </section>

      <!-- History -->
      <section v-if="tab === 'history'">
        <div v-if="!review.logs.length" class="apcwo-pr-banner">
          Nothing recorded yet.
        </div>
        <div v-for="(l, i) in review.logs" :key="i" class="apcwo-pr-log">
          <strong>{{ actionLabel(l.action) }}</strong>
          <span class="apcwo-pr-card__meta">
            {{ l.at }} · {{ l.actor || 'unknown' }}
            <template v-if="l.fromStatus !== l.toStatus">
              · {{ l.fromStatus }} → {{ l.toStatus }}
            </template>
          </span>
          <div v-if="l.note">{{ l.note }}</div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import {APIService} from '@ohrm/core/util/services/api.service';
import HrNav from '../components/HrNav.vue';
import StatusBadge from '../components/StatusBadge.vue';
import DownloadButtons from '../components/DownloadButtons.vue';
import EvaluationForm from '../components/EvaluationForm.vue';
import {API_BASE, errorMessage} from '../util/helpers';

const ACTION_LABELS = {
  save_draft: 'Employee started',
  submit: 'Employee submitted',
  unlock: 'HR unlocked',
  save_hr: 'Final review saved',
  save_manager: 'Manager evaluation saved',
  complete: 'Final review sent to employee',
  reopen: 'HR reopened evaluation',
};

export default {
  name: 'ReviewDetail',
  components: {
    'hr-nav': HrNav,
    'status-badge': StatusBadge,
    'download-buttons': DownloadButtons,
    'evaluation-form': EvaluationForm,
  },
  props: {
    isHr: {
      type: Boolean,
      default: false,
    },
    reviewId: {
      type: Number,
      required: true,
    },
  },
  setup() {
    const http = new APIService(
      window.appGlobal.baseUrl,
      `${API_BASE}/reviews`,
    );
    return {http};
  },
  data() {
    return {
      review: null,
      schema: null,
      hr: null,
      mine: null,
      tab: ['employee', 'mine', 'managers', 'hr', 'history'].includes(
        new URLSearchParams(window.location.search).get('tab'),
      )
        ? new URLSearchParams(window.location.search).get('tab')
        : 'employee',
      isLoading: false,
      unlocking: false,
      unlockReason: '',
    };
  },
  computed: {
    tabs() {
      const tabs = [{key: 'employee', label: 'Employee submission'}];
      if (this.review && this.review.myManagerEvaluation) {
        tabs.push({key: 'mine', label: 'My evaluation'});
      }
      if (this.isHr) {
        tabs.push(
          {key: 'managers', label: 'Manager evaluations'},
          {key: 'hr', label: 'Final review'},
          {key: 'history', label: 'History'},
        );
      }
      return tabs;
    },
    hrEditable() {
      return !!(this.review && this.review.permissions.editHr);
    },
  },
  created() {
    this.isLoading = true;
    this.http
      .get(this.reviewId)
      .then((response) => this.apply(response.data.data))
      .finally(() => {
        this.isLoading = false;
      });
  },
  methods: {
    apply(review) {
      this.review = review;
      this.schema = review.schema;
      this.hr = review.hrData
        ? JSON.parse(JSON.stringify(review.hrData))
        : null;
      this.mine = review.myManagerEvaluation
        ? JSON.parse(JSON.stringify(review.myManagerEvaluation))
        : null;
      if (!this.tabs.some((t) => t.key === this.tab)) {
        this.tab = this.mine ? 'mine' : 'employee';
      }
    },
    actionLabel(action) {
      return ACTION_LABELS[action] || action;
    },
    saveSection(section, data) {
      this.isLoading = true;
      return this.http
        .update(this.reviewId, {section, data})
        .then((response) => {
          this.apply(response.data.data);
          this.$toast.saveSuccess();
          return true;
        })
        .catch(() => false)
        .finally(() => {
          this.isLoading = false;
        });
    },
    async saveAndComplete() {
      if (await this.saveSection('hr', this.hr)) this.runAction('complete');
    },
    runAction(action, note) {
      this.isLoading = true;
      new APIService(
        window.appGlobal.baseUrl,
        `${API_BASE}/reviews/${this.reviewId}/actions`,
      )
        .create({action, note: note || ''})
        .then((response) => {
          this.apply(response.data.data);
          this.unlocking = false;
          this.unlockReason = '';
          this.$toast.success({title: 'Done', message: 'Review updated.'});
        })
        .catch((e) =>
          this.$toast.error({title: 'Error', message: errorMessage(e)}),
        )
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>

<style>
.apcwo-pr-detail-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
}
.apcwo-pr-read {
  padding: 12px 14px;
  border: 1px solid #e3e3ea;
  border-radius: 8px;
  font-size: 14px;
}
.apcwo-pr-read p {
  margin: 2px 0 10px 0;
  white-space: pre-wrap;
}
.apcwo-pr-read__pair {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}
.apcwo-pr-hr-row {
  margin-bottom: 14px;
}
.apcwo-pr-hr-row__top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 6px;
}
.apcwo-pr-potential {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}
.apcwo-pr-potential__opt {
  padding: 8px 14px;
  border: 1px solid #c4c4d0;
  border-radius: 8px;
  background: #fff;
  font-size: 14px;
  cursor: pointer;
}
.apcwo-pr-potential__opt.is-on {
  border-color: #741e1e;
  background: #f1eefa;
  font-weight: 700;
}
.apcwo-pr-log {
  padding: 8px 0;
  border-bottom: 1px solid #eee;
  font-size: 14px;
}
.apcwo-pr-root {
  font-family: 'Nunito Sans', sans-serif;
}
.apcwo-pr-root button,
.apcwo-pr-root input,
.apcwo-pr-root textarea,
.apcwo-pr-root select {
  font-family: inherit;
}
</style>
